import {
  buildRolloverQuote,
  formatDurationDays,
  isCarryoverEligible,
  isInGrace,
  loanGraceDays,
  loanOutstanding,
  maxLoanDaysForLevel,
  MIN_LOAN_DAYS,
  type Loan,
} from "@/lib/types";
import { useAuth, formatNaira } from "@/lib/auth";
import {
  fetchLoansForBorrower,
  fetchPlatformExpressSchedule,
  initiateRepayment,
  initiateRolloverInterestPayment,
  paymentLinkFrom,
  quoteLoanRollover,
} from "@/lib/data";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, ErrorBanner, Field, Input } from "@/components/ui-kit";

export const Route = createFileRoute("/borrower/repay")({
  component: RepayPage,
});

const totalDue = (l: Loan) => l.proposedRepay ?? l.amount * (1 + l.interestRate / 100);

function RepayPage() {
  const { session } = useAuth();
  const borrowerId = session?.kind === "borrower" ? session.borrower.id : null;
  const [loans, setLoans] = useState<Loan[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [amountStr, setAmountStr] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rolloverLoading, setRolloverLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [rollDays, setRollDays] = useState("");
  const [rollRepay, setRollRepay] = useState("");
  const [expressSchedule, setExpressSchedule] = useState<
    Awaited<ReturnType<typeof fetchPlatformExpressSchedule>>
  >([]);

  const load = useCallback(async () => {
    if (!borrowerId) return;
    const [rows, schedule] = await Promise.all([
      fetchLoansForBorrower(borrowerId),
      fetchPlatformExpressSchedule(),
    ]);
    const repayable = rows.filter((l) => l.status === "active" || l.status === "overdue");
    setLoans(repayable);
    setExpressSchedule(schedule);
    setSelectedId((prev) => (repayable.some((l) => l.id === prev) ? prev : repayable[0]?.id || ""));
  }, [borrowerId]);

  useEffect(() => {
    load().catch((e) => setError(e instanceof Error ? e.message : "Failed to load loans."));
  }, [load]);

  const loanRaw = loans.find((l) => l.id === selectedId) ?? null;
  const remaining = loanRaw ? Math.max(0, totalDue(loanRaw) - loanRaw.paid) : 0;

  useEffect(() => {
    if (!loanRaw) {
      setAmountStr("");
      return;
    }
    setAmountStr(remaining > 0 ? String(Math.round(remaining * 100) / 100) : "");
    setRollDays(String(loanRaw.durationDays || 14));
    setRollRepay("");
  }, [selectedId, remaining, loanRaw]);

  const borrowerLevel = session?.kind === "borrower" ? session.borrower.verificationLevel : "none";
  const loan = loanRaw
    ? {
        ...loanRaw,
        verificationLevel:
          loanRaw.verificationLevel ?? (borrowerLevel !== "none" ? borrowerLevel : undefined),
        graceDays:
          loanRaw.graceDays ??
          loanGraceDays({
            ...loanRaw,
            verificationLevel:
              loanRaw.verificationLevel ?? (borrowerLevel !== "none" ? borrowerLevel : undefined),
          }),
      }
    : null;
  const inGrace = loan ? isInGrace(loan) : false;
  const carryoverEligible = loan ? isCarryoverEligible(loan) : false;
  const graceDays = loan ? loanGraceDays(loan) : 0;

  const rollQuote = useMemo(() => {
    if (!loan || !carryoverEligible) return null;
    const days = Number(rollDays) || loan.durationDays;
    const proposed = rollRepay.trim() ? Number(rollRepay) : undefined;
    const isExpress = loan.verificationLevel === "express";
    const isElite = loan.verificationLevel === "elite";
    return buildRolloverQuote(loan, {
      durationDays: days,
      proposedRepay:
        isElite && proposed != null && Number.isFinite(proposed) ? proposed : undefined,
      // Express weekly bands only — never for Verified/Elite
      expressSchedule: isExpress ? expressSchedule : undefined,
    });
  }, [loan, carryoverEligible, rollDays, rollRepay, expressSchedule]);

  if (session?.kind !== "borrower") return null;

  const submit = async () => {
    setError(null);
    if (!loan) return setError("Select a loan.");
    if (remaining <= 0) return setError("This loan is already fully paid.");

    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      return setError("Enter how much you want to pay.");
    }
    if (amount > remaining + 0.01) {
      return setError(`Amount cannot exceed the outstanding balance of ${formatNaira(remaining)}.`);
    }
    if (amount < 100) {
      return setError("Minimum repayment is ₦100.");
    }

    setLoading(true);
    try {
      const nextUnpaid = loan.installments.find((i) => !i.paid);
      const data = await initiateRepayment({
        loanId: loan.id,
        amount,
        repaymentId: nextUnpaid?.id,
      });
      const link = paymentLinkFrom(data);
      if (!link) throw new Error("No payment link returned.");
      // Same tab — required on iOS so Paystack can redirect back to finalize
      window.location.assign(link);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Repayment failed.");
    } finally {
      setLoading(false);
    }
  };

  const startRollover = async () => {
    setError(null);
    if (!loan || !borrowerId || !rollQuote) return;
    if (rollQuote.canNegotiate && rollRepay.trim()) {
      const p = Number(rollRepay);
      if (!Number.isFinite(p) || p < rollQuote.remaining) {
        return setError("Negotiated repay must be at least the remaining balance.");
      }
    }
    const days = Number(rollDays);
    const maxDays = maxLoanDaysForLevel(loan.verificationLevel ?? "express");
    if (!days || days < MIN_LOAN_DAYS || days > maxDays) {
      return setError(`Duration must be between ${MIN_LOAN_DAYS} and ${maxDays} days.`);
    }

    setRolloverLoading(true);
    try {
      await quoteLoanRollover(loan.id, borrowerId, {
        durationDays: days,
        proposedRepay: rollRepay.trim() ? Number(rollRepay) : undefined,
      });
      const data = await initiateRolloverInterestPayment({
        loanId: loan.id,
        durationDays: days,
        proposedRepay: rollRepay.trim() ? Number(rollRepay) : undefined,
      });
      const link = paymentLinkFrom(data);
      if (!link) throw new Error("No payment link returned.");
      // Same tab — required on iOS so Paystack can redirect back to finalize
      window.location.assign(link);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start rollover.");
    } finally {
      setRolloverLoading(false);
    }
  };

  if (loans.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16">
        <Card>
          <h1 className="font-display text-3xl">Nothing to repay</h1>
          <p className="mt-3 text-muted-foreground">You don't have any active loans right now.</p>
          <Link to="/borrower" className="mt-4 inline-block text-gold hover:underline">
            ← Back to dashboard
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Repayment</div>
        <h1 className="font-display text-4xl mt-1">Pay down a loan</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          You can repay any amount anytime before the due date — full balance or parts over several
          payments.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {loans.map((l) => {
          const rem = Math.max(0, totalDue(l) - l.paid);
          const pct = (l.paid / Math.max(1, totalDue(l))) * 100;
          const active = l.id === selectedId;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => setSelectedId(l.id)}
              className={`text-left rounded-xl p-5 border transition glass ${active ? "border-gold/60 shadow-gold" : "border-border"}`}
            >
              <div className="flex justify-between items-start">
                <div className="font-mono text-xs text-muted-foreground">
                  {l.id.slice(0, 8).toUpperCase()}
                </div>
                <Badge tone={l.status === "overdue" ? "danger" : "gold"}>{l.status}</Badge>
              </div>
              <div className="mt-2 font-display text-2xl text-gold">{formatNaira(rem)}</div>
              <div className="text-xs text-muted-foreground">
                remaining of {formatNaira(totalDue(l))} · due {new Date(l.dueDate).toLocaleDateString()}
              </div>
              <div className="mt-3 h-1.5 rounded-full bg-accent overflow-hidden">
                <div className="h-full bg-gradient-gold" style={{ width: `${pct}%` }} />
              </div>
            </button>
          );
        })}
      </div>

      <Card>
        {done && (
          <div className="rounded-md border border-success/40 bg-success/10 px-3 py-2 text-sm text-success mb-4">
            Payment page opened — complete payment in the new tab. Your balance updates after
            Paystack confirms.
          </div>
        )}
        {inGrace && (
          <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning mb-4">
            Your loan is past due but still within the {graceDays}-day grace period.
            {carryoverEligible
              ? " You can repay or request a rollover below."
              : loan?.verificationLevel === "express"
                ? " Express loans cannot be rolled over — please repay as soon as you can."
                : " Please repay as soon as you can."}
          </div>
        )}
        <ErrorBanner message={error} />
        {loan && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Outstanding</div>
                <div className="font-display text-xl text-gold">{formatNaira(remaining)}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Due by</div>
                <div className="font-display text-xl">
                  {new Date(loan.dueDate).toLocaleDateString()}
                </div>
              </div>
            </div>
            <Field label="Amount to pay now (₦)">
              <Input
                type="number"
                min={100}
                max={remaining}
                step="100"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder="Enter any amount"
              />
            </Field>
            <div className="flex flex-wrap gap-2">
              {[0.25, 0.5].map((pct) => {
                const amt = Math.round(remaining * pct * 100) / 100;
                if (amt < 100) return null;
                return (
                  <Button
                    key={pct}
                    type="button"
                    variant="outline"
                    onClick={() => setAmountStr(String(amt))}
                    disabled={remaining < 100}
                  >
                    {Math.round(pct * 100)}% · {formatNaira(amt)}
                  </Button>
                );
              })}
              <Button
                type="button"
                variant="outline"
                onClick={() => setAmountStr(String(Math.round(remaining * 100) / 100))}
                disabled={remaining < 100}
              >
                Full balance
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Due date is the deadline — you do not have to wait until then to repay.
            </p>
            <Button
              variant="gold"
              loading={loading}
              onClick={submit}
              className="w-full sm:w-auto"
              disabled={remaining < 100}
            >
              Pay {amountStr ? formatNaira(Number(amountStr) || 0) : "now"}
            </Button>
          </div>
        )}
      </Card>

      {loan && carryoverEligible && rollQuote && (
        <Card>
          <h2 className="font-display text-xl">Rollover</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            You still owe {formatNaira(rollQuote.remaining)}. Pay the interest shown below now to
            extend the loan — that {formatNaira(rollQuote.remaining)} stays as what you owe, with a
            new due date.
          </p>
          <div className="mt-4 space-y-3">
            <Field
              label={`New tenure (days) · max ${maxLoanDaysForLevel(loan.verificationLevel ?? "express")}`}
            >
              <Input
                type="number"
                min={MIN_LOAN_DAYS}
                max={maxLoanDaysForLevel(loan.verificationLevel ?? "express")}
                value={rollDays}
                onChange={(e) => setRollDays(e.target.value)}
              />
            </Field>
            {rollQuote.canNegotiate && (
              <Field
                label="Propose new total repay (₦) — Elite only, optional"
                hint="Leave blank to use your loan’s interest rate on the remaining balance."
              >
                <Input
                  type="number"
                  min={rollQuote.remaining}
                  value={rollRepay}
                  onChange={(e) => setRollRepay(e.target.value)}
                  placeholder={`At least ${Math.round(rollQuote.remaining)}`}
                />
              </Field>
            )}
            <div className="rounded-md border border-border bg-accent/20 px-3 py-3 text-sm space-y-1">
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">New capital (remaining)</span>
                <span>{formatNaira(rollQuote.remaining)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-muted-foreground">
                  Interest to pay now ({rollQuote.ratePercent}%)
                </span>
                <span className="text-gold">{formatNaira(rollQuote.interestDue)}</span>
              </div>
              <div className="flex justify-between gap-4 font-medium">
                <span>New repayable after rollover</span>
                <span>{formatNaira(rollQuote.newRepayable)}</span>
              </div>
              <div className="text-xs text-muted-foreground pt-1">
                New due ≈ {formatDurationDays(rollQuote.durationDays)} from payment confirmation
              </div>
            </div>
            <Button variant="outline" loading={rolloverLoading} onClick={startRollover}>
              Pay {formatNaira(rollQuote.interestDue)} interest &amp; roll over
            </Button>
          </div>
        </Card>
      )}

      {loan && (
        <Card>
          <h2 className="font-display text-xl">Schedule</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {formatDurationDays(loan.durationDays)} term · due by{" "}
            {new Date(loan.dueDate).toLocaleDateString()} · paid {formatNaira(loan.paid)} of{" "}
            {formatNaira(totalDue(loan))}
            {loanOutstanding(loan) > 0 ? ` · ${formatNaira(loanOutstanding(loan))} left` : ""}
          </p>
          <div className="mt-4 space-y-2">
            {loan.installments.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No fixed instalments required. You can pay any amount toward your outstanding
                balance at any time, but the full balance must be paid on or before the due date.
              </p>
            ) : (
              loan.installments.map((r, i) => (
                <div
                  key={r.id}
                  className="flex justify-between text-sm py-2 border-b border-border/40"
                >
                  <span className="text-muted-foreground">
                    #{i + 1} · due by {new Date(r.dueDate).toLocaleDateString()}
                  </span>
                  <span>
                    {formatNaira(r.amount)} · {r.paid ? "Paid" : "Open"}
                  </span>
                </div>
              ))
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
